/**
 * UI density preference — Auto (default) / Comfortable / Compact / Enterprise.
 * Auto: CSS viewport + container queries (density-auto.css, typography-fluid.css).
 * Touch floor: control.css + density*.css + touch.css @media (pointer: coarse).
 * Touch always wins over compact/auto remaps (Responsive Spec §9).
 */
import { kvGetRaw, kvSetRaw } from '../infrastructure/storage/kvAccess';

export const DENSITY_STORAGE_KEY = 'aman_ui_density';

/** User-selectable modes — auto first (recommended). */
export const DENSITY_USER_MODES = ['auto', 'comfortable', 'compact', 'enterprise'];

const DEFAULT_DENSITY = 'auto';

const AUTO_COMPACT_MQ = '(max-width: 1279.98px), (max-height: 899.98px)';
const AUTO_ENTERPRISE_MQ = '(min-width: 1600px) and (min-height: 900px)';

const listeners = new Set();
let autoMediaListenersBound = false;

function notify() {
  listeners.forEach((fn) => {
    try {
      fn(readDensityPreference());
    } catch {
      /* ignore */
    }
  });
}

function safeMatchMedia(query) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return { matches: false, addEventListener: () => {} };
  }
  return window.matchMedia(query);
}

function bindAutoViewportListeners() {
  if (autoMediaListenersBound || typeof window === 'undefined') return;
  autoMediaListenersBound = true;
  const compactMq = safeMatchMedia(AUTO_COMPACT_MQ);
  const enterpriseMq = safeMatchMedia(AUTO_ENTERPRISE_MQ);
  const onChange = () => {
    if (readDensityPreference() === 'auto') notify();
  };
  compactMq.addEventListener?.('change', onChange);
  enterpriseMq.addEventListener?.('change', onChange);
}

/**
 * Resolved density for JS logic when mode is auto (matches density-auto.css breakpoints).
 */
export function resolveEffectiveDensity(mode = readDensityPreference()) {
  if (mode !== 'auto') return mode;
  if (typeof window === 'undefined') return 'comfortable';
  if (safeMatchMedia(AUTO_ENTERPRISE_MQ).matches) return 'enterprise';
  if (safeMatchMedia(AUTO_COMPACT_MQ).matches) return 'compact';
  return 'comfortable';
}

export function readDensityPreference() {
  try {
    const stored = kvGetRaw(DENSITY_STORAGE_KEY);
    if (DENSITY_USER_MODES.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  return DEFAULT_DENSITY;
}

export function writeDensityPreference(mode) {
  const next = DENSITY_USER_MODES.includes(mode) ? mode : DEFAULT_DENSITY;
  try {
    kvSetRaw(DENSITY_STORAGE_KEY, next);
  } catch {
    /* ignore quota */
  }
  applyDensityPreference(next);
  notify();
  return next;
}

export function applyDensityPreference(mode = readDensityPreference()) {
  if (typeof document === 'undefined') return mode;
  const resolved = DENSITY_USER_MODES.includes(mode) ? mode : DEFAULT_DENSITY;
  document.documentElement.setAttribute('data-density', resolved);
  bindAutoViewportListeners();
  return resolved;
}

export function subscribeDensityPreference(listener) {
  listeners.add(listener);
  bindAutoViewportListeners();
  return () => listeners.delete(listener);
}

export function isCompactDensity(mode = readDensityPreference()) {
  return resolveEffectiveDensity(mode) === 'compact';
}

export function initDensityPreference() {
  applyDensityPreference(readDensityPreference());
}
